import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircleQuestion, X } from "lucide-react";
import AssistantMessage from "./AssistantMessage";
import AssistantHeader from "./AssistantHeader";
import AssistantEmptyState from "./AssistantEmptyState";
import AssistantComposer from "./AssistantComposer";

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1 py-1" data-testid="assistant-typing">
      {[0, 1, 2].map((i) => (
        <span key={i} className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#1E3A8A]" style={{ animationDelay: `${i * 120}ms` }} />
      ))}
    </span>
  );
}

function MessageBubble({ message }) {
  const isUser = message.role === "user";
  let body = <TypingDots />;
  if (isUser) body = <p className="text-sm">{message.content}</p>;
  else if (message.content) body = <AssistantMessage content={message.content} />;
  return (
    <div data-testid={`assistant-msg-${message.role}`} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[88%] rounded-2xl border-2 px-3.5 py-2.5 ${
          isUser
            ? "rounded-br-md border-[#1E3A8A] bg-[#1E3A8A] text-white"
            : "rounded-bl-md border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-text)]"
        }`}
      >
        {body}
      </div>
    </div>
  );
}

export default function AssistantPanel({ open, onToggle, messages, meta, streaming, error, onSend, onNewChat, isPremium, stepContext, raised = false }) {
  const bottomRef = useRef(null);
  const remaining = Math.max(meta.daily_limit - meta.used_today, 0);
  const limitReached = meta.daily_limit > 0 && remaining === 0;
  const hasStepQuestions = !!stepContext?.questions?.length;
  const emptyStateQuestions = hasStepQuestions ? stepContext.questions : meta.suggested_questions;
  // On the eBook page the pagination bar sits at the bottom on mobile, so lift the button/panel above it
  const toggleOffset = raised ? "bottom-[5.75rem] sm:bottom-6" : "bottom-6";
  const panelOffset = raised ? "bottom-[10rem] sm:bottom-24" : "bottom-24";

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            key="assistant-panel"
            data-testid="assistant-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className={`fixed inset-x-3 ${panelOffset} z-50 flex max-h-[min(70vh,640px)] flex-col overflow-hidden rounded-2xl border-2 border-[var(--c-border)] bg-[var(--c-bg)] shadow-[6px_6px_0px_var(--c-shadow)] sm:inset-x-auto sm:right-6 sm:max-h-[min(78vh,640px)] sm:w-[400px]`}
          >
            <AssistantHeader onNewChat={onNewChat} onToggle={onToggle} />

            <div data-testid="assistant-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.length === 0 && (
                <AssistantEmptyState
                  stepContext={hasStepQuestions ? stepContext : null}
                  questions={emptyStateQuestions}
                  isPremium={isPremium}
                  onSend={onSend}
                />
              )}
              {messages.map((m) => <MessageBubble key={m.id} message={m} />)}
              {error && <p data-testid="assistant-error" className="text-xs font-semibold text-red-600">{error}</p>}
              <div ref={bottomRef} />
            </div>

            <AssistantComposer
              open={open}
              streaming={streaming}
              limitReached={limitReached}
              remaining={remaining}
              dailyLimit={meta.daily_limit}
              showStrip={hasStepQuestions && messages.length > 0}
              stepContext={stepContext}
              onSend={onSend}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="assistant-toggle"
        onClick={onToggle}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.95 }}
        aria-label={open ? "Close assistant" : "Open LLC Guide Assistant"}
        className={`fixed ${toggleOffset} right-4 z-50 flex h-14 items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-4 text-white shadow-[4px_4px_0px_var(--c-shadow)] sm:right-6`}
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircleQuestion className="h-5 w-5" />}
        <span className="hidden text-sm font-bold sm:inline">{open ? "Close" : "Ask the Guide"}</span>
      </motion.button>
    </>
  );
}
